// Paket A12 — Hinweis „Bestandsschutz endet" (Kind `bestandsschutz_end`).
//
// ⚠️ KEIN TEIL DES MITGLIEDSCHAFTS-PROMO-SYSTEMS.
// Der Host informiert darüber, dass das Plus aus dem Bestandsschutz ausläuft —
// eine Information zu einer zugesagten Leistung, keine Werbung. Deshalb hängt er
// weder am PromoProvider noch am MembershipPromoHost: eigener Endpunkt
// (api/bestandsschutzEnd.ts), eigener Server-Zustand (Tabelle
// bestandsschutz_end_notice), eigene Routen-Ausnahmen, KEINE Sitzungs-Caps,
// Cooldowns, Snooze- oder Startsperren des Promo-Systems. Wird das
// Promo-System später abgeschaltet (Intro, Start-Promos, Kontext-/Kontingent-
// Banner), läuft dieser Hinweis unverändert weiter. Diese Entkopplung ist
// Absicht — beim Abschalten der Promos NICHT hier mit anfassen.
//
// Ablauf
//   1. Der Server nennt die fällige Stufe (7 Tage vor Ende, 1 Tag vor Ende, am
//      Ende-Tag) oder nichts.
//   2. Sobald der Hinweis tatsächlich gezeigt werden dürfte (kein Startup-Gate,
//      keine ruhige Route, Bestandsschutz-Dialog nicht offen), „beansprucht" der
//      Host die Stufe per POST /claim. NUR bei `claimed: true` erscheint die
//      Karte. Dadurch zeigt jede Stufe höchstens einmal — auch wenn zwei Geräte
//      gleichzeitig öffnen.
//   3. Die Karte ist eine ruhige, schließbare Hinweiskarte im Stil der übrigen
//      Promo-Hosts (PromoBanner): kein Modal, kein Countdown, nichts Blinkendes.
//
// Jugendschutz (UWG Anhang Nr. 28): Der Verweis „Mitgliedschaft ansehen" erscheint
// NUR, wenn der Server `can_buy` meldet (volljährig). Alle anderen Konten sehen
// ausschließlich den Hinweis und „Verstanden" — der Client entscheidet das nicht
// selbst und nimmt nur ein ausdrückliches `true` an.
//
// Datum: der Server nennt den Ende-Tag (`endDay`; ein Ende vor 06:00 Ortszeit zählt
// zum Vortag, siehe server/bestandsschutz-end-notice.js) — der Host formatiert nur.
//
// Admin-Vorschau: Einstellungen → Mitgliedschaft löst über ein Fenster-Ereignis
// (BESTANDSSCHUTZ_END_PREVIEW_EVENT) eine Karte mit Beispieldatum aus. Die Vorschau
// berührt weder Server-Zustand noch Claim.
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ackBestandsschutzEndNotice,
  BESTANDSSCHUTZ_END_PREVIEW_EVENT,
  claimBestandsschutzEndNotice,
  getBestandsschutzEndNotice,
  type BestandsschutzEndNotice,
  type BestandsschutzEndPreviewDetail,
} from '../api/bestandsschutzEnd';
import { getBestandsschutz } from '../api/membership';
import { useAuth } from '../auth/AuthProvider';
import { isGuestUser } from '../auth/guest';
import { PromoBanner } from '../components/membership/PromoBanner';
import { useT } from '../i18n';
import { useStartupGate } from './startupGateContext';
import styles from './BestandsschutzEndHost.module.css';

export const BESTANDSSCHUTZ_END_QUERY_KEY = ['bestandsschutz-end'] as const;

const DAY_MS = 24 * 60 * 60 * 1000;

// Eigene Liste statt der Promo-Regeln (siehe Kopfkommentar): Seiten, auf denen
// der Hinweis stören oder doppelt wäre — die Mitgliedschaft selbst, Coin-Kauf,
// Anmeldung, Einführung, Rechtstexte, Admin, Sofortstart.
const QUIET_ROUTES: readonly RegExp[] = [
  /^\/mitgliedschaft(\/|\?|$)/,
  /^\/coins(\/|\?|$)/,
  /^\/login(\/|\?|$)/,
  /^\/onboarding(\/|\?|$)/,
  /^\/legal(\/|\?|$)/,
  /^\/admin(\/|\?|$)/,
  /^\/first-scene(\/|\?|$)/,
];

export function isQuietRoute(pathname: string): boolean {
  return QUIET_ROUTES.some((pattern) => pattern.test(pathname));
}

const DAY_KEY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Ein Kalendertag 'YYYY-MM-DD' (vom Server als Ende-Tag entschieden) in der
 * Kontosprache. Der Tag wird als reines Datum formatiert (UTC-Mittag), nie durch
 * eine Zeitzone verschoben. null bei einem ungültigen Tag.
 */
function formatDayKey(dayKey: string, lang: string): string | null {
  const match = DAY_KEY.exec(dayKey);
  if (!match) return null;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const noon = new Date(Date.UTC(year, month - 1, day, 12));
  if (Number.isNaN(noon.getTime()) || noon.getUTCMonth() !== month - 1 || noon.getUTCDate() !== day) return null;
  for (const locale of [lang, undefined]) {
    try {
      return new Intl.DateTimeFormat(locale, { dateStyle: 'long', timeZone: 'UTC' }).format(noon);
    } catch {
      // nächster Versuch
    }
  }
  return dayKey;
}

/**
 * Datum in Kontosprache („25. Oktober 2026"). Mit `endDay` (Entscheidung des
 * Servers: ein Ende vor 06:00 Ortszeit zählt zum Vortag) wird genau dieser Tag
 * genannt. Ohne ihn rechnet die Funktion in der Kontozeitzone aus `endsAt`.
 * Unbekannte Zone oder Sprache fallen schrittweise zurück; im Notfall das
 * ISO-Datum — nie ein leerer Platzhalter im Satz.
 */
export function formatEndDate(endsAt: string, lang: string, timezone: string, endDay?: string): string {
  if (endDay) {
    const fromDay = formatDayKey(endDay, lang);
    if (fromDay) return fromDay;
  }
  const parsed = new Date(endsAt);
  if (Number.isNaN(parsed.getTime())) return '';
  const attempts: [string | undefined, Intl.DateTimeFormatOptions][] = [];
  if (timezone) attempts.push([lang, { dateStyle: 'long', timeZone: timezone }]);
  attempts.push([lang, { dateStyle: 'long' }], [undefined, { dateStyle: 'long' }]);
  for (const [locale, options] of attempts) {
    try {
      return new Intl.DateTimeFormat(locale, options).format(parsed);
    } catch {
      // nächster Versuch
    }
  }
  return parsed.toISOString().slice(0, 10);
}

interface BestandsschutzEndCardProps {
  stage: BestandsschutzEndNotice['stage'];
  /** Fertig formatiertes Enddatum (siehe formatEndDate). */
  date: string;
  /** Nur true für volljährige Konten (Serverentscheidung). */
  canBuy: boolean;
  preview?: boolean;
  onOpen: () => void;
  onDismiss: () => void;
}

/**
 * Die Karte selbst (reine Darstellung). Mit `canBuy`: „Mitgliedschaft ansehen"
 * plus leises „Verstanden". Ohne: ausschließlich „Verstanden" — kein
 * Kaufverweis (Jugendschutz, UWG Anhang Nr. 28). Das X schließt immer.
 */
export function BestandsschutzEndCard({ stage, date, canBuy, preview = false, onOpen, onDismiss }: BestandsschutzEndCardProps) {
  const { t } = useT();
  return (
    <div
      className={styles.dock}
      data-lorath-dock="bestandsschutz-end"
      data-testid="bestandsschutz-end-notice"
      data-stage={stage}
      data-preview={preview ? 'true' : undefined}
    >
      <PromoBanner
        id="bestandsschutz-end"
        title={t('bestandsschutz_end.title')}
        text={t('bestandsschutz_end.body', { date })}
        ctaLabel={canBuy ? t('bestandsschutz_end.cta_view') : t('bestandsschutz_end.ack')}
        ctaIcon={canBuy ? undefined : null}
        onCta={canBuy ? onOpen : onDismiss}
        secondaryLabel={canBuy ? t('bestandsschutz_end.ack') : undefined}
        onSecondary={canBuy ? onDismiss : undefined}
        onDismiss={onDismiss}
        fullText
        tier="plus"
        icon="info"
      />
    </div>
  );
}

export function BestandsschutzEndHost() {
  const auth = useAuth();
  const gate = useStartupGate();
  const location = useLocation();
  const navigate = useNavigate();
  const { lang } = useT();

  const userId = auth.user?.userId ?? null;
  const enabled = auth.status === 'authed' && !isGuestUser(auth.user ?? null);
  const isAdmin = Boolean(auth.user && (auth.user.is_admin || auth.user.role === 'admin'));

  const noticeQuery = useQuery({
    queryKey: [...BESTANDSSCHUTZ_END_QUERY_KEY, userId],
    queryFn: getBestandsschutzEndNotice,
    enabled,
    staleTime: 5 * 60_000,
    retry: 0,
  });
  // Gleicher Schlüssel wie im PromoProvider und im GiftHost — ein Abruf für alle.
  // Solange der Bestandsschutz-Dialog selbst ansteht, wartet die Karte.
  const bestandsschutz = useQuery({
    queryKey: ['bestandsschutz'],
    queryFn: getBestandsschutz,
    enabled,
    staleTime: 60_000,
  });

  const notice = noticeQuery.data ?? null;
  const quietRoute = isQuietRoute(location.pathname);
  const dialogOpen = bestandsschutz.isPending || bestandsschutz.data?.state === 'pending';
  const canShowNow = enabled && gate === 'none' && !quietRoute && !dialogOpen;

  const [shown, setShown] = useState<BestandsschutzEndNotice | null>(null);
  const [preview, setPreview] = useState<BestandsschutzEndNotice | null>(null);
  const claimKeyRef = useRef<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Kontowechsel: nichts vom vorigen Konto stehen lassen.
  useEffect(() => {
    claimKeyRef.current = null;
    setShown(null);
  }, [userId]);

  // Stufe beanspruchen, sobald sie wirklich sichtbar sein dürfte. Je Konto,
  // Mitgliedschaft und Stufe höchstens einmal je Sitzung; der Server vergibt
  // zusätzlich jede Stufe nur ein einziges Mal.
  useEffect(() => {
    if (!notice || !canShowNow) return;
    const key = `${userId}:${notice.membershipId}:${notice.stage}`;
    if (claimKeyRef.current === key) return;
    claimKeyRef.current = key;
    void claimBestandsschutzEndNotice(notice.stage)
      .then((claimed) => {
        if (claimed && mountedRef.current) setShown(notice);
      })
      .catch(() => {
        // Nicht beansprucht (offline, Serverfehler) → nichts anzeigen; der
        // nächste Abruf des Servers liefert die Stufe erneut.
        if (claimKeyRef.current === key) claimKeyRef.current = null;
      });
  }, [notice, canShowNow, userId]);

  // Admin-Vorschau aus Einstellungen → Mitgliedschaft.
  useEffect(() => {
    if (!isAdmin || typeof window === 'undefined') return undefined;
    const onPreview = (event: Event) => {
      const detail = (event as CustomEvent<BestandsschutzEndPreviewDetail | undefined>).detail;
      setPreview({
        kind: 'bestandsschutz_end',
        stage: 'd7',
        membershipId: 0,
        endsAt: new Date(Date.now() + 7 * DAY_MS).toISOString(),
        timezone: '',
        canBuy: detail?.canBuy === true,
      });
    };
    window.addEventListener(BESTANDSSCHUTZ_END_PREVIEW_EVENT, onPreview);
    return () => window.removeEventListener(BESTANDSSCHUTZ_END_PREVIEW_EVENT, onPreview);
  }, [isAdmin]);

  const active = preview ?? shown;
  if (!active || gate !== 'none' || quietRoute) return null;

  const isPreview = preview !== null;
  const date = formatEndDate(active.endsAt, lang, active.timezone, active.endDay);

  const close = (action: 'dismiss' | 'open') => {
    if (isPreview) {
      setPreview(null);
      return;
    }
    void ackBestandsschutzEndNotice(active.stage, action).catch(() => undefined);
    setShown(null);
  };
  const dismiss = () => close('dismiss');
  const open = () => {
    close('open');
    // Ist das 25-%-Jahresangebot (bestand25) noch einlösbar, bekommt die Kaufansicht
    // den Hinweis mit — sonst sähe das Konto genau hier einen schlechteren Preis, als
    // ihm zusteht (die Geschenk-Erinnerung verlinkt ebenfalls mit Rabatt).
    const discount = active.canBuy === true && active.discountAvailable === true ? '&discount=bestand25' : '';
    navigate(`/mitgliedschaft?tier=plus${discount}&src=bestandsschutz_end`);
  };

  return (
    <BestandsschutzEndCard
      stage={active.stage}
      date={date}
      canBuy={active.canBuy === true}
      preview={isPreview}
      onOpen={open}
      onDismiss={dismiss}
    />
  );
}
